import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

// ── Self-contained calculator — has its own state, never touches bill data ──
// ── Now draggable: grab the "CALCULATOR" header bar to move it anywhere ──
const CalculatorWidget = ({ onClose }) => {
  const [display, setDisplay] = useState("0");
  const [prevValue, setPrevValue] = useState(null);
  const [operator, setOperator] = useState(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);

  // ── DRAG STATE ──
  const CALC_WIDTH = 300;
  const CALC_HEIGHT = 480; // approx height, used only for initial centering
  const [position, setPosition] = useState(null); // null until we know window size
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const boxRef = useRef(null);

  // center the calculator on first mount
  useEffect(() => {
    const centerX = Math.max(0, (window.innerWidth - CALC_WIDTH) / 2);
    const centerY = Math.max(0, (window.innerHeight - CALC_HEIGHT) / 2);
    setPosition({ x: centerX, y: centerY });
  }, []);

  const handleDragStart = (e) => {
    // ignore drag start when clicking the close (✕) button
    if (e.target.dataset.noDrag) return;
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    dragRef.current = {
      dragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragRef.current.dragging) return;
      const maxX = window.innerWidth - CALC_WIDTH;
      const maxY = window.innerHeight - 40; // keep header visible near bottom
      let newX = e.clientX - dragRef.current.offsetX;
      let newY = e.clientY - dragRef.current.offsetY;
      newX = Math.min(Math.max(0, newX), Math.max(0, maxX));
      newY = Math.min(Math.max(0, newY), Math.max(0, maxY));
      setPosition({ x: newX, y: newY });
    };
    const handleMouseUp = () => {
      dragRef.current.dragging = false;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const clearAll = () => {
    setDisplay("0");
    setPrevValue(null);
    setOperator(null);
    setWaitingForNewValue(false);
  };

  const inputDigit = (digit) => {
    if (waitingForNewValue) {
      setDisplay(String(digit));
      setWaitingForNewValue(false);
    } else {
      setDisplay(display === "0" ? String(digit) : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForNewValue) {
      setDisplay("0.");
      setWaitingForNewValue(false);
      return;
    }
    if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const toggleSign = () => {
    setDisplay(String(parseFloat(display) * -1));
  };

  const inputPercent = () => {
    setDisplay(String(parseFloat(display) / 100));
  };

  const backspace = () => {
    if (display.length === 1 || (display.length === 2 && display.startsWith("-"))) {
      setDisplay("0");
    } else {
      setDisplay(display.slice(0, -1));
    }
  };

  const compute = (a, b, op) => {
    switch (op) {
      case "+": return a + b;
      case "-": return a - b;
      case "×": return a * b;
      case "÷": return b === 0 ? NaN : a / b;
      default: return b;
    }
  };

  const handleOperator = (nextOperator) => {
    const inputValue = parseFloat(display);

    if (prevValue == null) {
      setPrevValue(inputValue);
    } else if (operator) {
      const result = compute(prevValue, inputValue, operator);
      setDisplay(String(Number.isFinite(result) ? Math.round(result * 1e10) / 1e10 : "Error"));
      setPrevValue(Number.isFinite(result) ? result : null);
    }

    setWaitingForNewValue(true);
    setOperator(nextOperator);
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);
    if (operator == null || prevValue == null) return;

    const result = compute(prevValue, inputValue, operator);
    setDisplay(String(Number.isFinite(result) ? Math.round(result * 1e10) / 1e10 : "Error"));
    setPrevValue(null);
    setOperator(null);
    setWaitingForNewValue(true);
  };

  // ── KEYBOARD SUPPORT ──
  // Maps physical keyboard keys to the same actions as the on-screen buttons.
  // Re-subscribed on every relevant state change so it always uses the
  // latest display/prevValue/operator/waitingForNewValue via closures above.
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key;

      if (key >= "0" && key <= "9") {
        e.preventDefault();
        inputDigit(Number(key));
        return;
      }

      switch (key) {
        case ".":
          e.preventDefault();
          inputDecimal();
          break;
        case "+":
          e.preventDefault();
          handleOperator("+");
          break;
        case "-":
          e.preventDefault();
          handleOperator("-");
          break;
        case "*":
        case "x":
        case "X":
          e.preventDefault();
          handleOperator("×");
          break;
        case "/":
          e.preventDefault();
          handleOperator("÷");
          break;
        case "Enter":
        case "=":
          e.preventDefault();
          handleEquals();
          break;
        case "Backspace":
          e.preventDefault();
          backspace();
          break;
        case "Delete":
        case "Escape":
          // Escape clears the calculator rather than closing it, so it
          // doesn't fight with the ✕ button as the only close mechanism.
          e.preventDefault();
          clearAll();
          break;
        case "%":
          e.preventDefault();
          inputPercent();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [display, prevValue, operator, waitingForNewValue]);

  const btnBase = {
    border: "none",
    fontSize: "17px",
    fontWeight: "500",
    cursor: "pointer",
    height: "52px",
    borderRadius: "8px",
    transition: "background 0.1s ease",
  };
  const numBtn = { ...btnBase, background: "#3a3a3a", color: "#fff" };
  const funcBtn = { ...btnBase, background: "#5a5a5a", color: "#fff" };
  const opBtn = { ...btnBase, background: "#ff9f0a", color: "#fff", fontSize: "20px" };
  const equalsBtn = { ...opBtn, background: "#ff9f0a" };

  // don't render until we've computed the initial centered position
  if (!position) return null;

 return createPortal(
  <div
    ref={boxRef}
    style={{
      position: "fixed",
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: "300px",
      background: "#1c1c1e",
      borderRadius: "16px",
      padding: "20px",
      boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
     zIndex: 100000,
    }}
  >
        {/* ── Header — drag handle ── */}
        <div
          onMouseDown={handleDragStart}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <span style={{ color: "#aaa", fontSize: "13px", fontWeight: "600", letterSpacing: "0.5px" }}>
            CALCULATOR
          </span>
          <span
            data-no-drag="true"
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ color: "#aaa", cursor: "pointer", fontSize: "16px", padding: "2px 6px" }}
          >
            ✕
          </span>
        </div>

        {/* ── Display ── */}
        <div
          style={{
            background: "#000",
            borderRadius: "10px",
            padding: "18px 16px",
            marginBottom: "14px",
            textAlign: "right",
            fontSize: "36px",
            fontWeight: "300",
            color: "#fff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "'Roboto Mono', monospace",
          }}
        >
          {display}
        </div>

        {/* ── Keypad ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
          <button style={funcBtn} onClick={clearAll}>AC</button>
          <button style={funcBtn} onClick={toggleSign}>+/-</button>
          <button style={funcBtn} onClick={inputPercent}>%</button>
          <button style={opBtn} onClick={() => handleOperator("÷")}>÷</button>

          <button style={numBtn} onClick={() => inputDigit(7)}>7</button>
          <button style={numBtn} onClick={() => inputDigit(8)}>8</button>
          <button style={numBtn} onClick={() => inputDigit(9)}>9</button>
          <button style={opBtn} onClick={() => handleOperator("×")}>×</button>

          <button style={numBtn} onClick={() => inputDigit(4)}>4</button>
          <button style={numBtn} onClick={() => inputDigit(5)}>5</button>
          <button style={numBtn} onClick={() => inputDigit(6)}>6</button>
          <button style={opBtn} onClick={() => handleOperator("-")}>−</button>

          <button style={numBtn} onClick={() => inputDigit(1)}>1</button>
          <button style={numBtn} onClick={() => inputDigit(2)}>2</button>
          <button style={numBtn} onClick={() => inputDigit(3)}>3</button>
          <button style={opBtn} onClick={() => handleOperator("+")}>+</button>

          <button style={{ ...numBtn, gridColumn: "span 2" }} onClick={() => inputDigit(0)}>0</button>
          <button style={numBtn} onClick={inputDecimal}>.</button>
          <button style={equalsBtn} onClick={handleEquals}>=</button>

          <button style={{ ...funcBtn, gridColumn: "span 4" }} onClick={backspace}>
            ⌫ Backspace
          </button>
        </div>
       </div>,
    document.body
  );
};

export default CalculatorWidget;